import {
  Fragment,
  useMemo,
  type ReactNode,
} from 'react';
import {
  useQuery,
} from '@tanstack/react-query';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  AddCircleOutlineRounded,
  AutoAwesomeRounded,
  CheckCircleRounded,
  CloseRounded,
  CompareArrowsRounded,
  HistoryRounded,
  LoginRounded,
  PersonRounded,
  TaskAltRounded,
  UpdateRounded,
} from '@mui/icons-material';
import {
  listIndustrialWorkflowCaseEvents,
} from '../services/industrialWorkflowsService';
import {
  formatOperationalDate,
} from './IndustrialWorkflowDatePicker';
import type {
  IndustrialWorkflowAssignableUser,
  IndustrialWorkflowCase,
  IndustrialWorkflowCaseEvent,
  IndustrialWorkflowStageDefinition,
} from '../types/industrialWorkflows';

type Props = {
  workflowCase: IndustrialWorkflowCase | null;
  stages: IndustrialWorkflowStageDefinition[];
  assignableUsers: IndustrialWorkflowAssignableUser[];
  currentUserId: string | null;
  onClose: () => void;
};

type EventVisual = {
  title: string;
  category: string;
  description: string;
  color: string;
  icon: ReactNode;
};

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat(
    'es-ES',
    {
      dateStyle: 'short',
      timeStyle: 'short',
    },
  ).format(new Date(value));
}

function getDayKey(value: string) {
  return new Date(value)
    .toISOString()
    .slice(0, 10);
}

function formatDaySeparator(value: string) {
  return new Intl.DateTimeFormat(
    'es-ES',
    {
      dateStyle: 'medium',
    },
  ).format(new Date(value));
}

function getErrorMessage(error: unknown) {
  if (
    typeof error === 'object'
    && error !== null
    && 'response' in error
  ) {
    const response = (
      error as {
        response?: {
          data?: {
            detail?: string;
          };
        };
      }
    ).response;

    if (response?.data?.detail) {
      return response.data.detail;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'No se pudo cargar el historial.';
}

function readPayloadText(
  payload: Record<string, unknown>,
  key: string,
) {
  const value = payload[key];

  return typeof value === 'string'
    ? value
    : null;
}

function readPayloadArray(
  payload: Record<string, unknown>,
  key: string,
) {
  const value = payload[key];

  return Array.isArray(value)
    ? value
    : [];
}

function formatPayloadValue(value: unknown) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (typeof value === 'boolean') {
    return value ? 'Sí' : 'No';
  }

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  return String(value);
}

function formatCaseFieldUpdates(
  payload: Record<string, unknown>,
) {
  const updates =
    readPayloadArray(
      payload,
      'case_field_updates',
    );

  if (!updates.length) {
    return 'Se han actualizado datos del expediente desde un parte.';
  }

  return updates
    .slice(0, 6)
    .map((update) => {
      if (
        typeof update !== 'object'
        || update === null
      ) {
        return null;
      }

      const updatePayload =
        update as Record<string, unknown>;

      const label =
        typeof updatePayload.label === 'string'
          ? updatePayload.label
          : typeof updatePayload.code === 'string'
            ? updatePayload.code
            : 'Dato';

      return `${label}: ${formatPayloadValue(
        updatePayload.previous_value,
      )} → ${formatPayloadValue(
        updatePayload.new_value,
      )}`;
    })
    .filter(Boolean)
    .join(' · ');
}

const EVENT_TYPE_LABELS: Record<string, string> = {
  schedule_recalculated: 'Planificación recalculada',
  schedule_manual_replanned: 'Replanificación manual',
  stage_deadline_adjusted: 'Plazo operativo de etapa ajustado',
  task_deadline_adjusted: 'Plazo de tarea ajustado',
  external_followup_recorded: 'Seguimiento externo registrado',
};

function formatEventTypeLabel(eventType: string) {
  const mappedLabel =
    EVENT_TYPE_LABELS[eventType];

  if (mappedLabel) {
    return mappedLabel;
  }

  return eventType
    .split('_')
    .filter(Boolean)
    .map((segment) =>
      `${segment.charAt(0).toUpperCase()}${segment.slice(1)}`,
    )
    .join(' ');
}

function buildEventVisual(
  event: IndustrialWorkflowCaseEvent,
  stageNameById: Map<string, string>,
  actorLabel: string,
): EventVisual {
  const payload =
    event.payload ?? {};

  if (event.event_type === 'case_comment_added') {
    const comment =
      readPayloadText(
        payload,
        'comment',
      );

    return {
      title: 'Comentario interno',
      category: 'Comentario',
      description:
        comment
        ?? 'Se ha añadido un comentario interno.',
      color: '#38bdf8',
      icon: (
        <PersonRounded fontSize="small" />
      ),
    };
  }

  if (event.event_type === 'case_created') {
    const stageId =
      readPayloadText(
        payload,
        'stage_definition_id',
      );

    return {
      title: 'Expediente creado',
      category: 'Inicio',
      description: stageId
        ? `Etapa inicial: ${
            stageNameById.get(stageId)
            ?? 'Etapa inicial'
          }.`
        : 'Se ha creado el expediente.',
      color: '#4ade80',
      icon: (
        <AddCircleOutlineRounded
          fontSize="small"
        />
      ),
    };
  }

  if (event.event_type === 'stage_entered') {
    const stageId =
      readPayloadText(
        payload,
        'stage_definition_id',
      );

    return {
      title: 'Entrada en etapa',
      category: 'Movimiento',
      description: stageId
        ? `El expediente ha entrado en ${
            stageNameById.get(stageId)
            ?? 'la nueva etapa'
          }.`
        : 'El expediente ha entrado en una nueva etapa.',
      color: '#a78bfa',
      icon: (
        <LoginRounded fontSize="small" />
      ),
    };
  }

  if (event.event_type === 'stage_changed') {
    const fromStageId =
      readPayloadText(
        payload,
        'from_stage_definition_id',
      );

    const toStageId =
      readPayloadText(
        payload,
        'to_stage_definition_id',
      );

    const fromStageName =
      fromStageId
        ? stageNameById.get(fromStageId)
        : null;

    const toStageName =
      toStageId
        ? stageNameById.get(toStageId)
        : null;

    return {
      title: 'Cambio de etapa',
      category: 'Movimiento',
      description:
        fromStageName && toStageName
          ? `${fromStageName} → ${toStageName}`
          : 'El expediente ha cambiado de etapa.',
      color: '#38bdf8',
      icon: (
        <CompareArrowsRounded
          fontSize="small"
        />
      ),
    };
  }

  if (
    event.event_type
    === 'task_auto_created'
  ) {
    const title =
      readPayloadText(
        payload,
        'title',
      );

    return {
      title: 'Tarea automática creada',
      category: 'Automatización',
      description: title
        ? `Se ha generado la tarea «${title}».`
        : 'Se ha generado una tarea al entrar en la etapa.',
      color: '#c4b5fd',
      icon: (
        <AutoAwesomeRounded
          fontSize="small"
        />
      ),
    };
  }

  if (event.event_type === 'task_created') {
    const title =
      readPayloadText(
        payload,
        'title',
      );

    return {
      title: 'Tarea creada',
      category: 'Tarea',
      description: title
        ? `Se ha creado la tarea «${title}».`
        : 'Se ha creado una nueva tarea.',
      color: '#7dd3fc',
      icon: (
        <TaskAltRounded
          fontSize="small"
        />
      ),
    };
  }

  if (event.event_type === 'task_claimed') {
    const title =
      readPayloadText(
        payload,
        'title',
      );

    return {
      title: 'Tarea asignada',
      category: 'Responsabilidad',
      description: title
        ? `«${title}»`
        : `${actorLabel} se ha asignado una tarea disponible.`,
      color: '#60a5fa',
      icon: (
        <PersonRounded fontSize="small" />
      ),
    };
  }

  if (event.event_type === 'task_updated') {
    const status =
      readPayloadText(
        payload,
        'status',
      );

    const title =
      readPayloadText(
        payload,
        'title',
      );

    const taskDescription = title
      ? `«${title}»`
      : null;

    if (status === 'completed') {
      return {
        title: 'Tarea completada',
        category: 'Tarea',
        description:
          taskDescription
          ?? 'La tarea se ha marcado como completada.',
        color: '#4ade80',
        icon: (
          <CheckCircleRounded
            fontSize="small"
          />
        ),
      };
    }

    if (status === 'in_progress') {
      return {
        title: 'Tarea iniciada',
        category: 'Tarea',
        description:
          taskDescription
          ?? 'La tarea ha pasado a estar en curso.',
        color: '#38bdf8',
        icon: (
          <TaskAltRounded
            fontSize="small"
          />
        ),
      };
    }

    if (status === 'blocked') {
      return {
        title: 'Tarea bloqueada',
        category: 'Tarea',
        description:
          taskDescription
          ?? 'La tarea se ha marcado como bloqueada.',
        color: '#f87171',
        icon: (
          <UpdateRounded fontSize="small" />
        ),
      };
    }

    if (status === 'cancelled') {
      return {
        title: 'Tarea cancelada',
        category: 'Tarea',
        description:
          taskDescription
          ?? 'La tarea se ha marcado como cancelada.',
        color: '#94a3b8',
        icon: (
          <UpdateRounded fontSize="small" />
        ),
      };
    }

    return {
      title: 'Tarea actualizada',
      category: 'Tarea',
      description:
        taskDescription
        ?? 'Se han actualizado los datos de una tarea.',
      color: '#94a3b8',
      icon: (
        <UpdateRounded fontSize="small" />
      ),
    };
  }

  if (
    event.event_type
    === 'external_followup_recorded'
  ) {
    const note =
      readPayloadText(
        payload,
        'note',
      );

    const nextReviewAt =
      readPayloadText(
        payload,
        'next_review_at',
      );

    return {
      title: 'Seguimiento externo registrado',
      category: 'Seguimiento',
      description:
        `${
          note
          ?? 'Se ha registrado un seguimiento externo.'
        }${
          nextReviewAt
            ? ` Próxima revisión: ${formatOperationalDate(
                nextReviewAt,
              )}.`
            : ''
        }`,
      color: '#fbbf24',
      icon: (
        <UpdateRounded fontSize="small" />
      ),
    };
  }

  if (
    event.event_type
    === 'case_fields_updated_from_work_report'
  ) {
    return {
      title: 'Datos del expediente actualizados',
      category: 'Expediente',
      description:
        formatCaseFieldUpdates(payload),
      color: '#7dd3fc',
      icon: (
        <UpdateRounded fontSize="small" />
      ),
    };
  }

  if (event.event_type === 'case_updated') {
    return {
      title: 'Expediente actualizado',
      category: 'Expediente',
      description:
        'Se han actualizado los datos del expediente.',
      color: '#94a3b8',
      icon: (
        <UpdateRounded fontSize="small" />
      ),
    };
  }

  return {
    title: 'Actividad registrada',
    category: 'Actividad',
    description:
      `Tipo de actividad: ${formatEventTypeLabel(
        event.event_type,
      )}.`,
    color: '#94a3b8',
    icon: (
      <HistoryRounded fontSize="small" />
    ),
  };
}

export default function IndustrialWorkflowCaseHistoryDialog({
  workflowCase,
  stages,
  assignableUsers,
  currentUserId,
  onClose,
}: Props) {
  const eventsQuery = useQuery({
    queryKey: [
      'industrial-workflows-case-events',
      workflowCase?.id,
    ],
    queryFn: () =>
      listIndustrialWorkflowCaseEvents(
        workflowCase?.id as string,
      ),
    enabled: Boolean(workflowCase),
    retry: false,
    refetchOnMount: 'always',
  });

  const stageNameById = useMemo(
    () =>
      new Map(
        stages.map(
          (stage) => [
            stage.id,
            stage.name,
          ],
        ),
      ),
    [stages],
  );

  const userLabelById = useMemo(
    () =>
      new Map(
        assignableUsers.map(
          (user) => [
            user.user_id,
            user.full_name
            || user.email
            || user.user_id,
          ],
        ),
      ),
    [assignableUsers],
  );

  const sortedEvents = useMemo(
    () =>
      [
        ...(eventsQuery.data ?? []),
      ].sort(
        (left, right) =>
          new Date(
            left.created_at,
          ).getTime()
          - new Date(
            right.created_at,
          ).getTime(),
      ),
    [eventsQuery.data],
  );

  const currentStage =
    workflowCase
      ? stageNameById.get(
          workflowCase
            .current_stage_definition_id,
        )
      : null;

  return (
    <Dialog
      open={Boolean(workflowCase)}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      BackdropProps={{
        sx: {
          bgcolor: 'rgba(2,6,23,0.78)',
          backdropFilter: 'blur(6px)',
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          border: `1px solid ${alpha(
            '#38bdf8',
            0.22,
          )}`,
          bgcolor: '#0f1b2f',
          backgroundImage: 'none',
          boxShadow:
            '0 28px 80px rgba(0,0,0,0.48)',
          overflow: 'hidden',
          maxHeight:
            'calc(100vh - 48px)',
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 0,
          bgcolor: '#0f1b2f',
        }}
      >
        <Stack
          direction="row"
          spacing={1.2}
          alignItems="center"
          sx={{
            px: {
              xs: 2,
              md: 2.4,
            },
            py: 1.7,
            borderBottom: `1px solid ${alpha(
              '#94a3b8',
              0.14,
            )}`,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2.2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#38bdf8',
              bgcolor: alpha(
                '#0284c7',
                0.14,
              ),
              border: `1px solid ${alpha(
                '#38bdf8',
                0.24,
              )}`,
              flexShrink: 0,
            }}
          >
            <HistoryRounded />
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                color: '#f8fafc',
                fontSize: '1.1rem',
                fontWeight: 950,
              }}
            >
              Historial del expediente
            </Typography>

            <Typography
              sx={{
                color: '#94a3b8',
                fontSize: '0.8rem',
                mt: 0.15,
              }}
            >
              Trazabilidad cronológica de cambios y movimientos.
            </Typography>
          </Box>

          <IconButton
            aria-label="Cerrar historial"
            onClick={onClose}
            sx={{
              color: '#94a3b8',
              flexShrink: 0,
            }}
          >
            <CloseRounded />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          px: {
            xs: 2,
            md: 2.4,
          },
          py: 2,
          bgcolor: '#0f1b2f',
          overflowX: 'hidden',
        }}
      >
        <Stack spacing={1.7}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: `1px solid ${alpha(
                '#38bdf8',
                0.2,
              )}`,
              bgcolor: alpha(
                '#082f49',
                0.32,
              ),
              px: 1.45,
              py: 1.2,
            }}
          >
            <Stack
              direction={{
                xs: 'column',
                sm: 'row',
              }}
              justifyContent="space-between"
              spacing={1}
            >
              <Box>
                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 950,
                  }}
                >
                  {workflowCase?.title
                    ?? 'Expediente'}
                </Typography>

                <Typography
                  sx={{
                    color: '#7dd3fc',
                    fontSize: '0.78rem',
                    fontWeight: 850,
                    mt: 0.25,
                  }}
                >
                  {workflowCase?.reference_code
                    ?? '—'}
                </Typography>
              </Box>

              <Stack
                direction="row"
                spacing={0.7}
                flexWrap="wrap"
                useFlexGap
              >
                <Chip
                  size="small"
                  label={`Etapa actual: ${
                    currentStage ?? '—'
                  }`}
                  sx={{
                    color: '#cbd5e1',
                    bgcolor: alpha(
                      '#64748b',
                      0.16,
                    ),
                  }}
                />

                <Chip
                  size="small"
                  label={`${sortedEvents.length} movimientos`}
                  sx={{
                    color: '#bae6fd',
                    bgcolor: alpha(
                      '#0284c7',
                      0.16,
                    ),
                  }}
                />
              </Stack>
            </Stack>
          </Paper>

          {eventsQuery.isFetching ? (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                color: '#94a3b8',
                py: 0.5,
              }}
            >
              <CircularProgress size={18} />

              <Typography
                sx={{
                  fontSize: '0.86rem',
                }}
              >
                Cargando historial...
              </Typography>
            </Stack>
          ) : null}

          {eventsQuery.isError ? (
            <Alert
              severity="error"
              variant="outlined"
            >
              {getErrorMessage(
                eventsQuery.error,
              )}
            </Alert>
          ) : null}

          {!eventsQuery.isLoading
          && !eventsQuery.isError
          && !sortedEvents.length ? (
            <Alert
              severity="info"
              variant="outlined"
            >
              Todavía no hay actividad registrada.
            </Alert>
          ) : null}

          <Box
            sx={{
              position: 'relative',
              overflowX: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 6,
                bottom: 6,
                left: 17,
                width: '1px',
                bgcolor: alpha(
                  '#38bdf8',
                  0.18,
                ),
              },
            }}
          >
            <Stack spacing={1}>
              {sortedEvents.map(
                (event, eventIndex) => {
                  const previousEvent =
                    sortedEvents[eventIndex - 1];

                  const shouldShowDaySeparator =
                    !previousEvent
                    || getDayKey(previousEvent.created_at)
                      !== getDayKey(event.created_at);

                  const isOwnAuthoredEvent =
                    Boolean(
                      currentUserId
                      && event.actor_user_id
                      && event.actor_user_id === currentUserId,
                    );

                  const actorLabel =
                    event.actor_user_id
                      ? userLabelById.get(
                          event.actor_user_id,
                        )
                        ?? 'Usuario'
                      : 'Sistema';

                  const displayActorLabel =
                    isOwnAuthoredEvent
                      ? 'Tú'
                      : actorLabel;

                  const visual =
                    buildEventVisual(
                      event,
                      stageNameById,
                      actorLabel,
                    );

                  return (
                    <Fragment key={event.id}>
                      {shouldShowDaySeparator ? (
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns:
                              '36px minmax(0, 1fr)',
                            columnGap: 1.2,
                            alignItems: 'center',
                            position: 'relative',
                            minWidth: 0,
                            mt: eventIndex === 0 ? 0 : 0.85,
                            mb: 0.15,
                          }}
                        >
                          <Box />

                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Box
                              sx={{
                                flex: 1,
                                height: '1px',
                                bgcolor: alpha(
                                  '#94a3b8',
                                  0.14,
                                ),
                              }}
                            />

                            <Typography
                              sx={{
                                color: '#94a3b8',
                                fontSize: '0.74rem',
                                fontWeight: 900,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {formatDaySeparator(
                                event.created_at,
                              )}
                            </Typography>

                            <Box
                              sx={{
                                flex: 1,
                                height: '1px',
                                bgcolor: alpha(
                                  '#94a3b8',
                                  0.14,
                                ),
                              }}
                            />
                          </Stack>
                        </Box>
                      ) : null}

                    <Box
                      key={event.id}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns:
                          '36px minmax(0, 1fr)',
                        columnGap: 1.2,
                        alignItems: 'start',
                        position: 'relative',
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: visual.color,
                          bgcolor: alpha(
                            visual.color,
                            0.14,
                          ),
                          border: `1px solid ${alpha(
                            visual.color,
                            0.44,
                          )}`,
                          zIndex: 1,
                        }}
                      >
                        {visual.icon}
                      </Box>

                      <Paper
                        elevation={0}
                        sx={{
                          minWidth: 0,
                          borderRadius: 2.6,
                          border: `1px solid ${alpha(
                            visual.color,
                            0.18,
                          )}`,
                          bgcolor:
                            'rgba(15,23,42,0.72)',
                          px: 1.25,
                          py: 1.1,
                        }}
                      >
                        <Stack spacing={0.55}>
                          <Stack
                            direction="row"
                            spacing={0.7}
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <Typography
                              sx={{
                                color: '#f8fafc',
                                fontWeight: 900,
                              }}
                            >
                              {visual.title}
                            </Typography>

                            <Chip
                              size="small"
                              label={
                                visual.category
                              }
                              sx={{
                                color: visual.color,
                                bgcolor: alpha(
                                  visual.color,
                                  0.13,
                                ),
                                fontWeight: 850,
                              }}
                            />
                          </Stack>

                          <Typography
                            sx={{
                              color: '#cbd5e1',
                              fontSize: '0.87rem',
                              overflowWrap:
                                'anywhere',
                            }}
                          >
                            {visual.description}
                          </Typography>

                          <Typography
                            sx={{
                              color: '#64748b',
                              fontSize: '0.76rem',
                            }}
                          >
                            {displayActorLabel}
                            {' · '}
                            {formatDateTime(
                              event.created_at,
                            )}
                          </Typography>
                        </Stack>
                      </Paper>
                    </Box>
                    </Fragment>
                  );
                },
              )}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: {
            xs: 2,
            md: 2.4,
          },
          py: 1.35,
          bgcolor: '#0f1b2f',
          borderTop: `1px solid ${alpha(
            '#94a3b8',
            0.12,
          )}`,
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            fontWeight: 850,
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}